import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { RestaurantFormData } from "@/types/restaurantForm";
import { useMapboxGeocoding } from "@/hooks/useMapboxGeocoding";
import { useState, useCallback } from "react";
import { toast } from "sonner";

interface AddressFieldProps {
  form: UseFormReturn<RestaurantFormData>;
  restaurantId?: number;
}

export const AddressField = ({ form, restaurantId }: AddressFieldProps) => {
  const [localInput, setLocalInput] = useState(form.getValues('address') || "");
  
  const { 
    predictions, 
    handleInputChange: debouncedHandleInputChange, 
    handlePlaceSelect,
    isLoading 
  } = useMapboxGeocoding({
    onSelect: (place) => {
      console.log('AddressField - Selected place:', {
        place_name: place.place_name,
        latitude: place.center[1],
        longitude: place.center[0]
      });
      
      form.setValue('address', place.place_name, { shouldValidate: true });
      form.setValue('latitude', place.center[1]); // Mapbox returns [lng, lat]
      form.setValue('longitude', place.center[0]);
      
      setLocalInput(place.place_name);
      toast.success("Address updated successfully");
    }
  });

  // Memoize the input handler to prevent unnecessary re-renders
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalInput(value);
    form.setValue('address', value, { shouldValidate: true });
    debouncedHandleInputChange(e);
  }, [form, debouncedHandleInputChange]);

  return (
    <FormField
      control={form.control}
      name="address"
      render={({ field }) => (
        <FormItem className="relative">
          <FormLabel>Address</FormLabel>
          <FormControl>
            <Input
              {...field}
              value={localInput}
              onChange={handleInputChange}
              placeholder="Start typing address..."
              disabled={isLoading}
              autoComplete="off"
            />
          </FormControl>
          {predictions.length > 0 && (
            <ul className="absolute z-10 w-full bg-background border rounded-md mt-1 shadow-lg max-h-[300px] overflow-y-auto">
              {predictions.map((prediction) => (
                <li
                  key={prediction.id}
                  className="px-4 py-2 hover:bg-accent cursor-pointer"
                  onClick={() => handlePlaceSelect(prediction)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{prediction.text}</span>
                    <span className="text-sm text-muted-foreground">{prediction.place_name}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};